import { TimePicker } from "@/components/time-picker/TimePicker.tsx";
import { TimePickerMode } from "@/components/time-picker/time-picker.types.ts";
import useFormatter, { formatDurationTitle } from "@/hooks/use-formatter.hook.ts";
import i18n from "@/i18n.ts";
import { MeetingFormValues } from "@/pages/workflows/edit/actions/meeting/workflow-meeting.schema.ts";
import {
  addMinutesToTimeString,
  durationBetweenTimeStrings,
  meetingDateToTimeString,
  meetingTimeStringToDate,
} from "@/pages/workflows/edit/actions/workflow-meeting.helpers.ts";
import { Time } from "@/types/common.types.ts";
import { Box, Button, FontAwesomeIcon, Select, SelectItem, Stack, Text } from "@skailer-com/uikit";
import { useState } from "react";
import { Control, useController } from "react-hook-form";
import { useTranslation } from "react-i18next";

const standardDurations = [15, 30, 45, 60, 90, 120];

const startTimes: string[] = [];
for (let minutes = 9 * 60; minutes <= 21 * 60; minutes += 30) {
  const hh = String(Math.floor(minutes / 60)).padStart(2, "0");
  const mm = String(minutes % 60).padStart(2, "0");
  startTimes.push(`${hh}:${mm}:00`);
}

interface MeetingTimeFieldProps {
  control: Control<MeetingFormValues>;
}

export const MeetingTimeField = ({ control }: MeetingTimeFieldProps) => {
  const { t } = useTranslation();
  const { renderTime } = useFormatter();

  const {
    field: { value: startTime, onChange: onStartChange },
    fieldState: { error: startError },
  } = useController({ control, name: "start_time" });
  const {
    field: { value: duration, onChange: onDurationChange },
    fieldState: { error: durationError },
  } = useController({ control, name: "duration_minutes" });

  const [isCustom, setIsCustom] = useState(() => !startTimes.includes(startTime));
  const [endError, setEndError] = useState<string>();

  const language = i18n.resolvedLanguage ?? i18n.language;

  const endDateAfter = (minutes: number) =>
    meetingTimeStringToDate(addMinutesToTimeString(startTime, minutes))!;

  const startDate = meetingTimeStringToDate(startTime);
  const endDate = endDateAfter(duration);

  const durations =
    duration > 0 && !standardDurations.includes(duration)
      ? [...standardDurations, duration].sort((a, b) => a - b)
      : standardDurations;

  const durationItem = (minutes: number): Time => {
    const date = endDateAfter(minutes);
    return {
      id: minutes,
      title: formatDurationTitle(minutes, language),
      caption: renderTime(date),
      date,
    };
  };

  const durationOptions: SelectItem[] = durations.map(durationItem);
  const endPresets: Time[] = standardDurations.map(durationItem);

  const handleEndChange = (date: Date) => {
    const nextDuration = durationBetweenTimeStrings(startTime, meetingDateToTimeString(date));
    onDurationChange(nextDuration);
    setEndError(nextDuration <= 0 ? t("errors:end_date_should_be_greater_start_date") : undefined);
  };

  return (
    <Stack gap={"sm"}>
      <Text className={"text-secondary text-sm"}>
        {t("calendar:event_start_time")} <span className={"text-danger"}>*</span>
      </Text>

      <Box className={"grid xs:grid-cols-3 grid-cols-5 gap-xs"}>
        {startTimes.map((time) => {
          const selected = !isCustom && startTime === time;
          return (
            <Button
              key={time}
              size={"sm"}
              variant={selected ? "primary" : "secondary"}
              leftIcon={selected ? <FontAwesomeIcon icon={"fa-light fa-check"} /> : undefined}
              onClick={() => {
                onStartChange(time);
                setIsCustom(false);
                setEndError(undefined);
              }}
            >
              {renderTime(meetingTimeStringToDate(time)!)}
            </Button>
          );
        })}
        <Button
          size={"sm"}
          variant={isCustom ? "primary" : "secondary"}
          onClick={() => {
            setIsCustom(true);
            setEndError(undefined);
          }}
        >
          {t("common:custom")}
        </Button>
      </Box>
      {startError?.message && <Text className={"text-danger text-sm"}>{startError.message}</Text>}

      {!isCustom ? (
        <Select
          label={t("common:duration")}
          emptyTitle={t("common:duration")}
          options={durationOptions}
          showCaption
          captionPerItem
          value={duration}
          renderSelectedTitle={(items) => {
            const selected = items[0];
            if (!selected) return null;
            return (
              <span className={"font-normal"}>
                <strong className={"font-semibold"}>{selected.title}</strong>
                {startDate && endDate
                  ? ` (${renderTime(startDate)} – ${renderTime(endDate)})`
                  : null}
              </span>
            );
          }}
          onChange={(value) => {
            if (value == null) return;
            onDurationChange(Number(value));
          }}
          error={durationError?.message}
        />
      ) : (
        <Box className={"grid grid-cols-2 gap-md"}>
          <TimePicker
            mode={TimePickerMode.single}
            label={t("calendar:event_start_time")}
            required
            showMask
            selectTimePartOnFocus
            value={startDate}
            onChange={(date) => onStartChange(meetingDateToTimeString(date))}
            error={startError?.message}
          />
          <TimePicker
            mode={TimePickerMode.single}
            label={t("calendar:event_end_time")}
            required
            showMask
            selectTimePartOnFocus
            presetItems={endPresets}
            navigatePresetsWithArrows
            value={endDate}
            onChange={handleEndChange}
            error={endError ?? durationError?.message}
          />
        </Box>
      )}
    </Stack>
  );
};
